
import { toInstant } from '@shared/tz';

export const SLOT_MINUTES = 30;
export const SLOT_MS = SLOT_MINUTES * 60 * 1000;
export const SLOTS_PER_DAY = (24 * 60) / SLOT_MINUTES;

export const DEFAULT_BOOKING_HORIZON_DAYS = 7;

export type Instant = Date | string;

export interface BookingRange {
  readonly start: Date;
  readonly end: Date;
}

export interface BookingWindow {
  readonly windowStart: Date;
  readonly windowEnd: Date;
}

export { toInstant };

export function isSlotAligned(value: Instant): boolean {
  return toInstant(value).getTime() % SLOT_MS === 0;
}

export function isValidBookingRange(range: BookingRange): boolean {
  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
  if (endMs <= startMs) return false;
  return startMs % SLOT_MS === 0 && endMs % SLOT_MS === 0;
}

export function bookingWindow(now: Instant, horizonDays: number): BookingWindow {
  const start = toInstant(now);
  const end = new Date(start.getTime() + horizonDays * 24 * 60 * 60 * 1000);
  return { windowStart: start, windowEnd: end };
}

export type WindowCheck = 'within' | 'in_past' | 'beyond_horizon';

export function classifyAgainstWindow(range: BookingRange, window: BookingWindow): WindowCheck {
  if (range.start.getTime() < window.windowStart.getTime()) return 'in_past';
  if (range.end.getTime() > window.windowEnd.getTime()) return 'beyond_horizon';
  return 'within';
}

export function isWithinWindow(range: BookingRange, window: BookingWindow): boolean {
  return classifyAgainstWindow(range, window) === 'within';
}

export function toTstzRangeLiteral(range: BookingRange): string {
  return `[${range.start.toISOString()},${range.end.toISOString()})`;
}
